/**
 * Physical console/corbel layout in metres (shared by 2D layout + STM ghost overlay).
 *
 * Layout x: 0 = column face, increasing toward the beam tip.
 * Backend bearing FE: x = 0 at beam outer tip, x = L toward column — so contact near the column
 * maps to small layout x (after construction tolerance), not to the far tip.
 *
 * @param padDepth - Along-corbel length of the **contact** patch (L_eff direct; pad thickness neoprene).
 * @param bearingSeatLength_m - Optional. Direct bearing: nominal strip length L_nom on corbel (beam seat).
 *   If omitted, inferred from db.corbel_horizontal_m − tolerance or padDepth.
 */

export function computeConsoleLayoutMeters({
  neoprenePadOn,
  tolerance,
  padDepth,
  bearingSeatLength_m: bearingSeatLengthMIn,
  eX,
  conHeight,
  colDepth,
  minCover,
  mainDiameterMm,
  hDiameterMm,
  beamLength,
  db,
  stmNode1X,
  /** STM visualization N1 y (negative = below top); ties physical bend depth to the model */
  stmNode1Y,
}) {
  const d_m = mainDiameterMm / 1000;
  const d_s = hDiameterMm / 1000;
  const cover = minCover;
  const bendFactor = mainDiameterMm <= 16 ? 4 : 7;
  const R_mandrel = (bendFactor * d_m) / 2;

  /** Nominal bearing extent along corbel from column face (after gap): L_nom, not L_eff. */
  const seatLen_m = (() => {
    if (neoprenePadOn) return padDepth;
    if (bearingSeatLengthMIn != null && Number.isFinite(bearingSeatLengthMIn) && bearingSeatLengthMIn > 0) {
      return bearingSeatLengthMIn;
    }
    if (db?.corbel_horizontal_m != null && Number.isFinite(db.corbel_horizontal_m)) {
      return Math.max(padDepth, db.corbel_horizontal_m - tolerance);
    }
    return Math.max(padDepth, 0.2);
  })();
  const seatOuter_m = tolerance + seatLen_m;

  /** STM / FE: pressure resultant (column face → load); may sit near inner edge under liftoff. */
  const loadX_stm_m =
    stmNode1X != null && Number.isFinite(stmNode1X)
      ? stmNode1X
      : neoprenePadOn
        ? tolerance + padDepth / 2 + eX
        : db?.cantilever_arm ?? tolerance + padDepth / 2;

  let bearingLeft;
  let bearingRight;
  let cx1;
  let cy1;

  if (neoprenePadOn) {
    bearingLeft = tolerance;
    bearingRight = tolerance + padDepth;
    const x_pad_edge = bearingRight;
    const dist_nose = cover + R_mandrel + d_m;
    cx1 = x_pad_edge + dist_nose;
    cy1 = conHeight - (cover + d_m / 2 + R_mandrel);
  } else {
    // Contact patch sits next to the column (inner side of the seat), not at the far tip.
    // Dark strip = L_eff along corbel; beam block spans the nominal seat length L_nom.
    bearingLeft = tolerance;
    bearingRight = tolerance + padDepth;
    // Inboard offset from N1 to bend centreline (cx1): mandrel + bar. Top/side cover is already
    // enforced in cy1 / column leg — do not subtract cover again in x (that drew the tie ~cover
    // short of N1, e.g. ~100 mm vs x_c ≈ 140 mm for Ø16).
    const bend_clear_inboard = R_mandrel + d_m;
    cx1 = Math.max(0.02, loadX_stm_m - bend_clear_inboard);
    cy1 = conHeight - (cover + d_m / 2 + R_mandrel);
  }

  const loadX_mid_contact_m = (bearingLeft + bearingRight) / 2;
  /** Physical layout diagram: centroid of drawn contact strip (matches L_eff pad). */
  const loadX_display_m = neoprenePadOn ? loadX_stm_m : loadX_mid_contact_m;
  const loadX_m = loadX_stm_m;

  if (stmNode1Y != null && Number.isFinite(stmNode1Y)) {
    const tieCenterY = conHeight + stmNode1Y;
    cy1 = tieCenterY - R_mandrel;
  }

  const cx2 = 0;
  const cy2 = 0;
  const R2 = cover + d_m / 2 + d_s;
  const dx = cx1 - cx2;
  const dy = cy1 - cy2;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const theta_c = Math.atan2(dy, dx);

  let alpha_t = 0;
  if (dist > R_mandrel + R2) {
    alpha_t = Math.asin((R_mandrel + R2) / dist);
  }

  const normalAngle1 = theta_c - Math.PI / 2 - alpha_t;
  const normalAngle2 = theta_c + Math.PI / 2 - alpha_t;
  const tx1 = cx1 + R_mandrel * Math.cos(normalAngle1);
  const ty1 = cy1 + R_mandrel * Math.sin(normalAngle1);
  const tx2 = cx2 + R2 * Math.cos(normalAngle2);
  const ty2 = cy2 + R2 * Math.sin(normalAngle2);

  const m_rebar = (ty1 - ty2) / (tx1 - tx2);
  const diag_offset = (d_m / 2 + d_s / 2) / Math.sin(Math.atan(Math.abs(m_rebar)));

  const x_nose_face = cx1 + R_mandrel + d_m / 2 + d_s + cover;
  const y_nose_bottom_limit = cy1 - R_mandrel - d_m / 2 - cover;
  const h_nose_concrete = Math.max(conHeight - y_nose_bottom_limit, cover * 2 + d_m + R_mandrel);
  const L_viz = x_nose_face;

  const geometryFallback =
    !neoprenePadOn && db && db.geometry_fallback_for_layout === true;

  /** Backend indicative horizontal reach (m): min geometric corbel when direct bearing failed. */
  const corbelReachFromApi_m =
    !neoprenePadOn && db?.corbel_horizontal_m != null && Number.isFinite(db.corbel_horizontal_m)
      ? db.corbel_horizontal_m
      : null;

  const loadXExtent_m = Math.max(loadX_stm_m, loadX_mid_contact_m);

  const L_corbel_m = geometryFallback
    ? Math.max(
        corbelReachFromApi_m ?? seatOuter_m,
        loadXExtent_m + 0.02,
        bearingRight + 0.005,
        0.12
      )
    : Math.max(
        L_viz,
        neoprenePadOn ? bearingRight + 0.05 : corbelReachFromApi_m ?? seatOuter_m,
        loadXExtent_m + 0.02,
        0.12
      );

  const y_top_bar = cy1 + R_mandrel;
  const x_back = -colDepth + cover;
  const col_center_x = -colDepth / 2;
  const min_turn_dist = R_mandrel + d_m / 2;
  const x_turn = Math.max(col_center_x, x_back + min_turn_dist);
  const y_turn = m_rebar * (x_turn - tx1) + ty1;

  const beamLeft_m = tolerance;
  const beamWidth_m = neoprenePadOn
    ? Math.max(beamLength, bearingRight - bearingLeft + Math.max(0.05, padDepth * 0.2))
    : Math.max(beamLength, seatLen_m);

  return {
    loadX_m,
    loadX_display_m,
    loadX_stm_m,
    L_corbel_m,
    bearingLeft,
    bearingRight,
    L_viz,
    cx1,
    cy1,
    tx1,
    ty1,
    tx2,
    ty2,
    x_turn,
    y_turn,
    y_top_bar,
    x_back,
    h_nose_concrete,
    m_rebar,
    d_m,
    d_s,
    R_mandrel,
    diag_offset,
    beamLeft_m,
    beamWidth_m,
    x_nose_face,
  };
}
